import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Trash2, Mail, LogIn, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import AppFooter from '../components/AppFooter';

const SUPPORT_EMAIL = 'support@selahslate.com';

export default function DeleteAccountPage() {
  return (
    <div className="min-h-screen bg-[#F5F5F5] flex flex-col" data-testid="delete-account-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                Selah Slate&trade;
              </span>
            </Link>
            <Link
              to="/"
              className="text-sm text-[#36454F]/70 hover:text-[#36454F] font-['Manrope']"
              data-testid="back-home-link"
            >
              Back to home
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex-1 w-full">
        <Link
          to="/"
          className="inline-flex items-center text-[#36454F]/65 hover:text-[#36454F] font-['Manrope'] mb-6 transition-colors text-sm"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Back to home
        </Link>

        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-sm bg-[#D9534F]/10 mb-4">
            <Trash2 className="w-6 h-6 text-[#D9534F]" strokeWidth={1.5} />
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#36454F]">
            Delete your Selah Slate account
          </h1>
          <p className="mt-2 text-[#36454F]/65 font-['Manrope']">
            You can permanently delete your account and all associated data at any time.
            This page complies with Google Play's data-deletion policy.
          </p>
        </div>

        {/* Option 1: In-app deletion */}
        <Card className="mb-6 border-[#36454F]/15 bg-white" data-testid="option-in-app">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-sm bg-[#4CAF50]/10 flex items-center justify-center flex-shrink-0">
                <LogIn className="w-5 h-5 text-[#4CAF50]" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] mb-2">
                  Option 1 — Delete in-app (instant)
                </h2>
                <p className="text-sm text-[#36454F]/75 font-['Manrope'] mb-4 leading-relaxed">
                  If you can still sign in, this is the fastest path. Your account and all data
                  are wiped immediately.
                </p>
                <ol className="space-y-2 text-sm text-[#36454F]/80 font-['Manrope'] mb-4 list-decimal list-inside">
                  <li>Sign in to your Selah Slate account.</li>
                  <li>Open <strong>Settings</strong> from the dashboard header.</li>
                  <li>Scroll to <strong>Danger Zone → Delete Account</strong>.</li>
                  <li>Enter your password to confirm and click <strong>Delete Account</strong>.</li>
                </ol>
                <Link to="/login">
                  <Button
                    className="bg-[#36454F] hover:bg-[#2a363f] text-white font-['Manrope'] rounded-sm"
                    data-testid="sign-in-to-delete-btn"
                  >
                    <LogIn className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Sign in to delete my account
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Option 2: Email request */}
        <Card className="mb-6 border-[#36454F]/15 bg-white" data-testid="option-email">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-sm bg-[#B76E79]/10 flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] mb-2">
                  Option 2 — Email request (within 30 days)
                </h2>
                <p className="text-sm text-[#36454F]/75 font-['Manrope'] mb-4 leading-relaxed">
                  Lost access to your account? Email us and we'll handle the deletion manually.
                </p>
                <ol className="space-y-2 text-sm text-[#36454F]/80 font-['Manrope'] mb-4 list-decimal list-inside">
                  <li>
                    Email <a href={`mailto:${SUPPORT_EMAIL}?subject=Account%20Deletion%20Request`}
                      className="text-[#B76E79] underline font-medium"
                      data-testid="support-email-link">{SUPPORT_EMAIL}</a> from the email tied to your account.
                  </li>
                  <li>Use the subject line <code className="px-1.5 py-0.5 bg-[#F5F5F5] rounded text-xs">Account Deletion Request</code>.</li>
                  <li>We verify ownership, delete your data, and send confirmation within 30 days (usually 1–2 business days).</li>
                </ol>
                <a
                  href={`mailto:${SUPPORT_EMAIL}?subject=Account%20Deletion%20Request&body=Please%20delete%20the%20Selah%20Slate%20account%20associated%20with%20this%20email%20address.%0A%0AI%20understand%20this%20is%20permanent%20and%20cannot%20be%20undone.`}
                  data-testid="email-deletion-btn"
                >
                  <Button
                    variant="outline"
                    className="border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white font-['Manrope'] rounded-sm"
                  >
                    <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Email deletion request
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* What gets deleted */}
        <Card className="mb-6 border-[#36454F]/15 bg-white" data-testid="what-gets-deleted">
          <CardContent className="p-6">
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] mb-4">
              What gets deleted
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                'Your profile (name, email, address, encrypted SSN last-4)',
                'All credit-report analyses and uploaded PDF metadata',
                'All saved dispute drafts',
                'All filed disputes, generated letters, and tracking history',
                'Your journal entries and personal milestones',
                'Your debt-calculator data and reflections',
                'Your testimonies (removed from the public Testimony Wall)',
                'Login sessions, password-reset tokens, and analytics IDs',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-[#36454F]/80 font-['Manrope']">
                  <CheckCircle className="w-4 h-4 text-[#4CAF50] flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* What may be retained */}
        <Card className="mb-6 border-[#B76E79]/30 bg-[#E0BFB8]/10" data-testid="what-retained">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#B76E79] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <h2 className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F] mb-2">
                  What may be retained
                </h2>
                <p className="text-sm text-[#36454F]/80 font-['Manrope'] leading-relaxed mb-2">
                  Some records are retained briefly to meet legal and operational obligations:
                </p>
                <ul className="space-y-1.5 text-sm text-[#36454F]/75 font-['Manrope']">
                  <li>• <strong>Aggregated, anonymized usage statistics</strong> (no personally identifying information) — kept indefinitely.</li>
                  <li>• <strong>Email delivery logs</strong> (timestamps only, no message bodies) — retained for 30 days by our email provider Resend.</li>
                  <li>• <strong>Server access logs</strong> (IP addresses, request paths) — auto-deleted after 14 days.</li>
                </ul>
                <p className="text-xs text-[#36454F]/55 font-['Manrope'] mt-3 italic">
                  None of these can be used to re-identify you after your account is deleted.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-[#36454F]/55 font-['Manrope']">
          Need help? Read our <Link to="/privacy" className="text-[#B76E79] underline">privacy policy</Link>{' '}
          or <Link to="/support" className="text-[#B76E79] underline">contact support</Link>.
        </p>
      </main>

      <AppFooter />
    </div>
  );
}
